import { useEffect, useRef, useState } from 'react'
import _L from 'leaflet'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const L = _L as any

// Leaflet's default marker icon resolves its image paths relative to the
// page URL, which breaks under Vite bundling (shows a broken-image icon).
// Building an explicit icon from the bundled assets avoids that -- same
// visual result as vanilla Leaflet, just resolved correctly by the bundler.
const markerDivIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

const LIMA_CENTER: [number, number] = [-12.05, -77.03]

// The lat/lng inputs below use step="0.000001" (6 decimals, matching the
// DECIMAL(9,6) columns this ends up in) -- Leaflet click/drag events and
// navigator.geolocation both return far more decimal digits than that, which
// makes the browser's native number-input validation block form submission
// with a "introduce un valor válido" step-mismatch error. Rounding at the
// point of capture keeps what's displayed consistent with what's actually
// stored, instead of letting Postgres silently round it later.
const roundCoord = (n: number) => Math.round(n * 1e6) / 1e6

interface LocationPickerProps {
  latitude: number | null
  longitude: number | null
  onChange: (lat: number | null, lng: number | null) => void
}

export default function LocationPicker({ latitude, longitude, onChange }: LocationPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const onChangeRef = useRef(onChange)
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)
  // Local text mirrors of the numeric inputs -- Lima's coordinates are both
  // negative (-12.05, -77.03), so a controlled <input type="number"> bound
  // directly to `latitude ?? ''` breaks the moment a user types just "-":
  // Number("-") is NaN, which isn't null/undefined, so `?? ''` doesn't catch
  // it and the field would show the literal text "NaN". Keeping the raw
  // in-progress string here lets "-", "-1", "-1." etc. render normally while
  // only propagating to onChange once the string parses to a real number.
  const [latText, setLatText] = useState(latitude != null ? String(latitude) : '')
  const [lngText, setLngText] = useState(longitude != null ? String(longitude) : '')

  // Only overwrite the text if it doesn't already represent this same
  // number -- otherwise correcting a digit mid-edit (e.g. "-12.05" ->
  // "-12.0") would round-trip through the parent's onChange, come back as
  // the reformatted `-12`, and stomp the trailing "." the user just typed
  // before they finish the next digit.
  useEffect(() => {
    const current = latText === '' ? null : Number(latText)
    if (current === latitude) return
    setLatText(latitude != null ? String(latitude) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude])

  useEffect(() => {
    const current = lngText === '' ? null : Number(lngText)
    if (current === longitude) return
    setLngText(longitude != null ? String(longitude) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longitude])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // Init map + single draggable marker once. Intentionally reads
  // latitude/longitude only at mount time (initial position) -- later
  // external changes are handled by the effect below, without recreating
  // the map (same pattern as SalesZonesMap.tsx / DistrictBubblesMap.tsx).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const hasInitial = latitude != null && longitude != null
    const initialCenter: [number, number] = hasInitial ? [latitude as number, longitude as number] : LIMA_CENTER
    const initialZoom = hasInitial ? 16 : 12

    const map = L.map(containerRef.current).setView(initialCenter, initialZoom)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map)

    const marker = L.marker(initialCenter, { draggable: true, icon: markerDivIcon }).addTo(map)

    marker.on('dragend', () => {
      const pos = marker.getLatLng()
      onChangeRef.current(roundCoord(pos.lat), roundCoord(pos.lng))
    })

    map.on('click', (e: any) => {
      const { lat, lng } = e.latlng
      marker.setLatLng([lat, lng])
      onChangeRef.current(roundCoord(lat), roundCoord(lng))
    })

    mapRef.current = map
    markerRef.current = marker

    setTimeout(() => map.invalidateSize(), 0)

    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reposition the marker (without recreating the map) when the
  // coordinates change from outside -- e.g. the user typed into the
  // numeric inputs, or "Usar mi ubicación actual" fired.
  useEffect(() => {
    if (!markerRef.current || !mapRef.current) return
    if (latitude == null || longitude == null) return
    markerRef.current.setLatLng([latitude, longitude])
    mapRef.current.panTo([latitude, longitude])
  }, [latitude, longitude])

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setGeoError('Tu navegador no soporta geolocalización.')
      return
    }
    setGeoError(null)
    setGeoLoading(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoLoading(false)
        onChange(roundCoord(pos.coords.latitude), roundCoord(pos.coords.longitude))
      },
      (err) => {
        setGeoLoading(false)
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError('Permiso de ubicación denegado. Habilítalo en la configuración del navegador para usar esta opción.')
        } else if (err.code === err.TIMEOUT) {
          setGeoError('Se agotó el tiempo esperando la ubicación. Intenta de nuevo.')
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoError('No se pudo determinar tu posición (señal GPS débil, común dentro de un local). Intenta acercarte a una ventana o ingresa las coordenadas manualmente.')
        } else {
          setGeoError('No se pudo obtener tu ubicación.')
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  const handleLatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setLatText(v)
    if (v === '') {
      onChange(null, longitude)
      return
    }
    const n = Number(v)
    if (!Number.isNaN(n)) onChange(n, longitude)
  }

  const handleLngChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value
    setLngText(v)
    if (v === '') {
      onChange(latitude, null)
      return
    }
    const n = Number(v)
    if (!Number.isNaN(n)) onChange(latitude, n)
  }

  return (
    <div className="space-y-2">
      <div
        ref={containerRef}
        style={{ width: '100%', height: '300px', borderRadius: '8px' }}
      />
      <button
        type="button"
        onClick={handleUseCurrentLocation}
        disabled={geoLoading}
        className="px-3 py-1.5 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100 disabled:opacity-50"
      >
        {geoLoading ? 'Obteniendo ubicación...' : 'Usar mi ubicación actual'}
      </button>
      {geoError && (
        <p className="text-xs text-red-600" role="alert">{geoError}</p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="location-picker-lat" className="block text-sm font-medium text-espresso-700 mb-1">Latitud</label>
          <input
            id="location-picker-lat"
            type="number"
            step="0.000001"
            min={-90}
            max={90}
            value={latText}
            onChange={handleLatChange}
            className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          />
        </div>
        <div>
          <label htmlFor="location-picker-lng" className="block text-sm font-medium text-espresso-700 mb-1">Longitud</label>
          <input
            id="location-picker-lng"
            type="number"
            step="0.000001"
            min={-180}
            max={180}
            value={lngText}
            onChange={handleLngChange}
            className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          />
        </div>
      </div>
    </div>
  )
}
